import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

// Shopify prices are stored in cents. Format as "1 250 kr".
function formatSEK(cents) {
  return Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' kr';
}

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * It also listens for pricing:changed events to switch between B2B (ex-VAT) and B2C (incl-VAT) pricing.
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Subscribe to VAT changes before the section check — the sticky add-to-cart bar moves
    // its product-price to <body>, outside any .shopify-section.
    window.addEventListener('pricing:changed', this.#onPricingChanged);
    document.addEventListener('section:rendered', this.#onSectionRendered);
    this.renderVat(window.pricingFormat);

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    window.removeEventListener('pricing:changed', this.#onPricingChanged);
    document.removeEventListener('section:rendered', this.#onSectionRendered);
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
  }

  #onPricingChanged = (e) => this.renderVat(e.detail);
  #onSectionRendered = () => this.renderVat(window.pricingFormat);

  #onVariantSelected = () => this.setAttribute('shimmer', '');

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = async (event) => {
    const section = this.closest('.shopify-section, dialog');
    await section?._inventoryReady;

    this.removeAttribute('shimmer');

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Copy raw price data attributes for VAT rendering
    for (const attr of ['data-price', 'data-compare-at-price', 'data-taxable', 'data-price-min', 'data-price-max']) {
      const val = newProductPrice.getAttribute(attr);
      if (val !== null) this.setAttribute(attr, val);
      else this.removeAttribute(attr);
    }

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }

    this.renderVat(window.pricingFormat);
  };

  renderVat(format) {
    if (!format || !window.VAT_CONFIG) return;

    const { taxRate } = window.VAT_CONFIG;
    const factor = 1 + taxRate / 100;
    const taxable = this.dataset.taxable !== 'false';
    const f = format === 'b2c' && taxable ? factor : 1;

    const container = this.querySelector('[ref="priceContainer"]');
    if (!container) return;

    const priceMin = parseInt(this.dataset.priceMin, 10);
    const priceMax = parseInt(this.dataset.priceMax, 10);

    if (!isNaN(priceMin) && !isNaN(priceMax)) {
      const rangeSpan = container.querySelector('.price-range');
      if (rangeSpan) rangeSpan.textContent = formatSEK(priceMin * f) + ' – ' + formatSEK(priceMax * f);
    } else {
      const price = parseInt(this.dataset.price, 10);
      if (!isNaN(price)) {
        container.querySelectorAll('.price:not(.price-range)').forEach(span => {
          span.textContent = formatSEK(price * f);
        });
      }
      const compareAt = parseInt(this.dataset.compareAtPrice, 10);
      if (!isNaN(compareAt)) {
        container.querySelectorAll('.compare-at-price').forEach(span => {
          span.textContent = formatSEK(compareAt * f);
        });
      }
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
